import React, { FC, useEffect, useState } from 'react'

import { maxTextLength, maxTextTitleLength } from 'tabs/nlp/constants'
import { TrainingStatus } from 'tabs/settings/constants/trainingStatus'

import * as S from './TableForm.style'
import { formatDateTime } from 'helpers/formatDate/formatDate'
import { noop } from 'lodash'
import { Option } from 'helpers/selects'
import { DeleteData } from 'tabs/data/components/DeleteData'
import { CButton } from 'tabs/data/components/CButton'
import { CreatableMultiselect } from 'tabs/data/components/CreatableMultiselect'
import { Prompt } from 'react-router-dom'

interface TableFormProps {
  trainingStatus?: string
  isFromEdit?: boolean
  inputText?: string
  inputDescription: string
  textDescription: string
  textAreaContent?: string
  cancelButtonLabel?: string
  submitButtonLabel?: string
  updatedAt?: number
  options: Option[]
  joinedTables: string[]
  areInitialValues?: boolean
  synonyms: string[]
  isActive?: boolean
  onSubmit: (
    title: string,
    text: string,
    joinedTables: string[],
    synonyms: string[],
    isActive: boolean,
  ) => Promise<void>
  onCancel?: () => void
  onDelete?: (forceDelete: boolean) => Promise<any>
}

export const TableForm: FC<TableFormProps> = ({
  trainingStatus,
  cancelButtonLabel,
  submitButtonLabel,
  areInitialValues,
  inputDescription,
  textDescription = 'Text',
  isFromEdit = false,
  inputText = '',
  synonyms: synonymsValue,
  joinedTables: joinedTablesValues,
  updatedAt,
  textAreaContent = '',
  isActive: isActiveValue = true,
  onSubmit,
  onCancel = noop,
  onDelete,
}) => {
  const [loading, setLoading] = useState(false)

  const [textTitle, setTextTitle] = useState(inputText || '')
  const [text, setText] = useState(textAreaContent)
  const [synonyms, setSynonyms] = useState<string[]>(synonymsValue || [])
  const [joinedTables, setJoinedTables] = useState<string[]>(joinedTablesValues || [])
  const [isActive, setIsActive] = useState(isActiveValue)
  const [errorText, setErrorText] = useState('')
  const [errorTitle, setErrorTitle] = useState('')
  const [errorAPI, setErrorAPI] = useState('')

  const formHasChanges =
    textTitle.trim() !== inputText.trim() ||
    text.trim() !== textAreaContent.trim() ||
    (joinedTables || []).toString() !== (joinedTablesValues || []).toString() ||
    (synonyms || []).toString() !== (synonymsValue || []).toString() ||
    isActive !== isActiveValue

  const formError = errorTitle || errorText || errorAPI

  const isSubmitButtonDisabled = loading || !text.trim() || formError || areInitialValues

  const handleTitleChange = e => {
    if (errorAPI) setErrorAPI('')
    const { value = '' } = e.target

    const isValidTitleLength = value.length <= maxTextTitleLength

    if (!isValidTitleLength) {
      setErrorTitle(`You've reached the max length for the text title value — ${maxTextTitleLength} characters`)
    } else if (value.trim().length <= 0) {
      setErrorTitle(`${inputDescription || 'Title'} can't be empty`)
    } else {
      setErrorTitle('')
    }

    setTextTitle(value)
  }

  const handleTextChange = e => {
    if (errorAPI) setErrorAPI('')
    const { value = '' } = e.target

    if (value.length >= maxTextLength) {
      setErrorText(`You've reached the max length for the text value — ${maxTextLength} characters`)
    } else if (value.trim().length <= 0) {
      setErrorText(`${textDescription} can't be empty`)
    } else {
      setErrorText('')
    }

    setText(value)
  }

  const handleReset = () => {
    setTextTitle(inputText)
    setText(textAreaContent)
    setSynonyms(synonymsValue || [])
    setJoinedTables(joinedTablesValues || [])
    setIsActive(isActiveValue)

    if (onCancel) {
      onCancel()
    }
  }

  const handleSubmit = async () => {
    if (loading) return

    try {
      setLoading(true)
      await onSubmit(textTitle.trim(), text.trim(), joinedTables, synonyms, isActive)
    } catch (e) {
      throw e
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (formHasChanges) window.onbeforeunload = () => true
    if (!formHasChanges) window.onbeforeunload = () => null

    return () => {
      window.onbeforeunload = null
    }
  }, [formHasChanges, isSubmitButtonDisabled])

  return (
    <S.Container isFromEdit={isFromEdit}>
      <Prompt
        when={formHasChanges}
        message="Are you sure you want to leave this page without saving your current changes?"
      />
      <S.Form>
        <S.Title isFromEdit={isFromEdit}>{inputDescription}*</S.Title>
        <S.InputContainer isError={Boolean(formError)} isFromEdit={isFromEdit}>
          <S.TitleInput disabled={loading} value={textTitle} placeholder="Enter title" onChange={handleTitleChange} />
        </S.InputContainer>
        <S.Error>{errorTitle}</S.Error>

        <S.Title isFromEdit={isFromEdit}>{textDescription}*</S.Title>
        <S.InputContainer isError={Boolean(formError)} isFromEdit={isFromEdit}>
          <S.Textarea
            disabled={loading}
            isFromEdit={isFromEdit}
            placeholder="+ Insert text here"
            value={text}
            onChange={handleTextChange}
            isError={formError}
          />
        </S.InputContainer>
        <S.Error>{errorText}</S.Error>

        <S.Title>
          Synonyms <small>(optional)</small>
        </S.Title>
        <CreatableMultiselect disabled={loading} options={synonymsValue} values={synonyms} onChange={setSynonyms} />

        <S.SwitchContainer>
          <S.HiddenCheckbox
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            disabled={loading}
          />
          <S.SwitchTrack checked={isActive} disabled={loading}>
            <S.SwitchThumb checked={isActive} />
          </S.SwitchTrack>
          <S.SwitchLabel checked={isActive}>{isActive ? 'Table is active' : 'Table is inactive'}</S.SwitchLabel>
        </S.SwitchContainer>

        <S.Footer>
          {updatedAt && (
            <small style={{ opacity: 0.75, marginRight: 'auto' }}>Updated at: {formatDateTime(updatedAt * 1000)}</small>
          )}
          <CButton color="secondary" onClick={handleReset}>
            {cancelButtonLabel || 'Cancel'}
          </CButton>
          <CButton onClick={handleSubmit} disabled={Boolean(!formHasChanges || isSubmitButtonDisabled)}>
            {submitButtonLabel || 'Add text'}
          </CButton>

          {onDelete && <DeleteData buttonLabel="Delete Table" onDelete={onDelete} />}
        </S.Footer>
      </S.Form>
      {trainingStatus === TrainingStatus.PENDING && <S.DisableScreen />}
    </S.Container>
  )
}
